<template>
  <CreativesApprovalCore
    ref="creativesApprovalCore"
    :title="title"
    :creatives="creatives"
    :items-total-count="creativesTotalCount"
    :allow-selection="false"
    :is-from-deal-details-view="isFromDealDetailsView"
    :columns="columns"
    :sort-column="columns.submittedOnFormatted"
    :fixed-filters="creativeFixedFilters"
    :table-id="creativesTableId"
    :is-loading="isLoading"
    :show-approve-button="showApproveButton"
    :show-reject-button="showRejectButton"
    :show-approve-for-specific-inventory-button="showApproveForSpecificInventoryButton"
    :selected-items-count="selectedCreativeFiles?.length"
    :text-search-properties="[`name`, `advertiser.name`]"
    @fetch-items="(queryParams) => fetchCreatives(queryParams)"
    @approve-selected-items="approveSelectedCreativeFiles"
    @reject-selected-items="rejectSelectedCreativeFiles"
    @approve-for-specific-inventory="approveCreativeFilesForSpecificInventory">
    <template #expandedRow="{ creative }">
      <CreativeFilesApproval
        :title="title"
        :is-external="true"
        :creatives-table-id="creativesTableId"
        :entity-id="creative.id"
        :creative-fixed-filters="creativeFixedFilters"
        :show-creative-loader="isLoading"
        :is-from-deal-details-view="isFromDealDetailsView"
        :allow-selection="allowSelection"
        :creative-files="creative.creativeFiles"
        :selected-creative-files="selectedCreativeFilesByCreative[creative.id] ?? []"
        @selection-changed="(selectedCreativeFiles) => onSelectedCreativeFilesChanged(creative, selectedCreativeFiles)">
        <template #action="{ item: creativeFile }">
          <CreativeFileRowActions
            :creative="creative"
            @prompt-creative-file-approval-for-specific-inventory="
              () => approveCreativeFilesForSpecificInventory([creativeFile])
            " />
        </template>
      </CreativeFilesApproval>
    </template>
    <template #link="{ creative }">
      <div class="item-name-cell">
        <div
          v-if="selectedCreativeFilesByCreative[creative.id]?.length > 0"
          class="selected-items-count-badge">
          {{ selectedCreativeFilesByCreative[creative.id].length }}
        </div>
        <span :data-entity-pending-creative="creative.name">
          {{ creative.name }}
        </span>
      </div>
    </template>
    <template #advertiserDescription="{ creative }">
      {{
        creative.advertiserDescription.toLowerCase().includes('unknown') ? 'UNKNOWN' : creative.advertiserDescription
      }}
    </template>
    <template #packageCreatives="{ creative }">
      <CreativePackages :creative="creative" />
    </template>
    <template #prefetchSource="{ creative }">
      {{ dspNameRef?.[creative.prefetchSource] || DASH }}
    </template>
    <template #pendingCreativeFiles="{ creative }">
      {{ creative.creativeFilePendingApprovalCount }} / {{ creative.creativeFileCount }}
    </template>
    <template #pendingScreenSizeApprovals="{ creative }">
      <template v-if="creative.prefetchAllSizesApproval != null">
        {{ creative.prefetchSizePendingApprovalCount }} / {{ creative.prefetchSizeCount }}
      </template>
      <template v-else>
        {{ $t('messages.notApplicable') }}
      </template>
    </template>
    <template #iabCategories="{ creative }">
      {{ creative.iabCategoriesFormatted }}
    </template>
    <template #languagesDescription="{ creative }">
      <h4 v-if="creative.languageIDs == null || creative.languageIDs.length === 0">
        <span class="languages-placeholder">
          {{ $t('messages.pleaseSelectALanguage') }}
        </span>
        <span
          v-tooltip.left="capitalize($t('messages.noLanguagesSelected'))"
          class="info">
          <icon-info />
        </span>
      </h4>
      <slot
        name="languages"
        :item="creative" />
    </template>
  </CreativesApprovalCore>
</template>

<script setup lang="ts">
  import { capitalize, computed, Ref, ref, useTemplateRef } from 'vue';
  import type { ComponentExposed } from 'vue-component-type-helpers';
  import { useRoute } from 'vue-router';

  import TableColumn from '@/common/components/controls/table/TableColumn';
  import TableColumnTypes from '@/common/components/controls/table/TableColumnTypes';
  import { IconInfo } from '@/common/icons/monochrome';
  import {
    Creative,
    CreativeFile,
    Filter,
    FilterCollection,
    FilterComparisonOperator,
    FilterGroup,
    FilterLogicalOperator,
    QueryParams,
  } from '@/common/models';
  import CreativeFileApprovalStatus from '@/common/models/creative/CreativeFileApprovalStatus.enum';
  import GlobalEventBus from '@/GlobalEventBus';
  import GlobalEvents from '@/GlobalEvents';
  import { DASH, getAppInstance } from '@/globals';
  import { CreativeMediaOwnerApprovalService } from '@/org-app/components/creatives/approval/CreativeMediaOwnerApprovalService';
  import CreativePackages from '@/org-app/components/creatives/approval/CreativePackages.vue';
  import CreativesApprovalCore from '@/org-app/components/creatives/approval/CreativesApprovalCore.vue';
  import CreativeFileRowActions from '@/org-app/components/creatives/CreativeFileRowActions.vue';
  import CreativeFilesApproval from '@/org-app/components/creatives/CreativeFilesApproval.vue';
  import { useAdvertisers, useOrganizationDSPs } from '@/org-app/services/tanstack/queries';
  import { runQueryInScope } from '@/org-app/services/tanstack/queryUtils';
  import { mapOrgAppActions, mapOrgAppState } from '@/utils/StateMapping';
  import UserNotifications from '@/utils/UserNotifications';
  import Validator from '@/validator/Validator';
  import i18n from '@/vueI18n';

  const route = useRoute();

  interface IProps {
    title: string;
    isFromDealDetailsView?: boolean;
    isPastApprovals?: boolean;
    dspNames?: Record<number, string>;
  }

  const { isFromDealDetailsView, isPastApprovals, dspNames } = defineProps<IProps>();

  interface IEmits {
    (event: 'approval-changed'): void;
  }

  const emits = defineEmits<IEmits>();

  const { organizationSettings } = mapOrgAppState();

  const creativesApprovalCore = useTemplateRef<ComponentExposed<typeof CreativesApprovalCore>>('creativesApprovalCore');
  const creatives: Ref<Creative[]> = ref([]);
  const creativesTotalCount = ref(0);
  const isLoading = ref(false);
  const selectedCreativeFilesByCreative = ref<Record<number, CreativeFile[]>>({});
  const organizationDSPsQuery = useOrganizationDSPs();

  const inventoryTargetingModal = computed(() => creativesApprovalCore.value?.inventoryTargetingModal);

  const allowSelection = computed(() => !isPastApprovals);

  const buyerOrganizationDSPs = computed(() => organizationDSPsQuery.items.value ?? []);
  const creativesTableId = computed(() => {
    return `${!isPastApprovals ? 'pending' : 'past'}_external_approvals_creatives_list_table`;
  });

  const selectedCreativeFiles = computed(() => Object.values(selectedCreativeFilesByCreative.value).flat());

  const creativeFixedFilters = computed(() => [
    new FilterCollection([
      new Filter('prefetch_type', [null], FilterComparisonOperator.NOT_EQUAL),
      new FilterGroup(
        [new Filter('creative_files.prefetch_approval', [CreativeFileApprovalStatus.PENDING])],
        allowSelection.value ? FilterLogicalOperator.OR : FilterLogicalOperator.NOT,
      ),
    ]),
  ]);

  const columns = computed(() => ({
    link: TableColumn.fromObject({
      label: capitalize(i18n.global.t('names.name', { n: 1 })),
      property: 'name',
      allowSort: true,
      useAsFilter: true,
    }),
    pendingScreenSizeApprovals: TableColumn.fromObject({
      label: capitalize(i18n.global.t('names.screenSizePendingApproval', { n: 2 })),
      allowSort: false,
      useAsFilter: false,
    }),
    ...(allowSelection.value && {
      pendingCreativeFiles: TableColumn.fromObject({
        label: capitalize(i18n.global.t('names.filePendingApproval', { n: 2 })),
        allowSort: false,
        useAsFilter: false,
      }),
    }),
    prefetchSource: TableColumn.fromObject({
      label: capitalize(i18n.global.t('names.prefetchSource', { n: 1 })),
      property: 'prefetch_source',
      allowSort: false,
      useAsFilter: dspNames != null && Object.keys(dspNames)?.length > 0,
      source: dspNames != null ? Object.keys(dspNames).map((x) => ({ id: x, name: dspNames[x] })) : [],
    }),
    prefetchID: TableColumn.fromObject({
      label: capitalize(i18n.global.t('names.prefetchID', { n: 1 })),
      property: 'prefetch_id',
      allowSort: true,
      useAsFilter: true,
    }),
    advertiserDescription: TableColumn.fromObject({
      label: capitalize(i18n.global.t('names.advertiser', { n: 1 })),
      property: 'advertiser.name',
      type: TableColumnTypes.integer,
      source: () => runQueryInScope(useAdvertisers),
      sourceFilterProperty: 'advertiser.advertiser_id',
      useAsFilter: true,
    }),
    packageCreatives: TableColumn.fromObject({
      label: capitalize(i18n.global.t('names.submissionSource')),
      allowSort: false,
      useAsFilter: false,
    }),
    iabCategories: TableColumn.fromObject({
      label: capitalize(i18n.global.t('names.iabCategory', { n: 2 })),
      property: `iabCategories`,
      allowSort: false,
      useAsFilter: false,
    }),
    languagesDescription: Creative.entityProperties.languages.getTableColumn({ allowSort: false, useAsFilter: false }),
    submittedOnFormatted: TableColumn.fromObject({
      label: capitalize(i18n.global.t('messages.submittedOn')),
      property: 'submitted_at',
      type: TableColumnTypes.date,
    }),
  }));

  const showApproveButton = computed(() =>
    selectedCreativeFiles.value.some((x) => x.prefetchApproval !== CreativeFileApprovalStatus.APPROVED),
  );

  const showRejectButton = computed(() =>
    selectedCreativeFiles.value.some((x) => x.prefetchApproval !== CreativeFileApprovalStatus.REJECTED),
  );

  const showApproveForSpecificInventoryButton = computed(() => {
    if (!organizationSettings?.useInventoryTargetingForCreativeApproval) {
      return false;
    }
    /**
     * External creatives can only be approved if the mediaOwnerApprovals are not empty
     * @see  https://hivestack.atlassian.net/browse/HV-8707
     */

    /**
     * In case of external creatives, selectedCreatives is an array of CreativeFile
     * @type {CreativeFile[]}
     */
    const canUseInventoryTargeting = selectedCreativeFiles.value.every((creativeFile) => {
      const creative = creatives.value.find((x) => x.id === creativeFile.creativeID);
      return creative != null && creative.mediaOwnerApprovals?.length > 0;
    });
    return canUseInventoryTargeting;
  });

  const dspNameRef = computed(() => {
    const buyers = buyerOrganizationDSPs.value
      .filter((x) => x.dsp != null && x.dsp.isExternal)
      .map((x) => ({
        id: x.dsp.id.toString().trim(),
        name: x.dsp.name,
      }));

    const uniqueBuyers = Array.from(new Set(buyers.map((x) => x.id))).map((x) => buyers.find((b) => b.id === x));

    return uniqueBuyers.reduce((r, x) => {
      r[x.id] = x.name;
      return r;
    }, {});
  });

  const { getPackageCreatives, getExternalCreatives, updateCreativeFilePrefetchApprovalState } = mapOrgAppActions();

  async function fetchCreatives(queryParams: QueryParams) {
    try {
      isLoading.value = true;
      selectedCreativeFilesByCreative.value = {};
      let result;
      if (isFromDealDetailsView) {
        const packageID = route.params.id;
        result = await getPackageCreatives({ id: packageID });
        creatives.value = result;
      } else {
        result = await queryParams.run(getExternalCreatives);
        creatives.value = result.items;
        creativesTotalCount.value = result.totalCount;
      }

      if (!isPastApprovals) {
        GlobalEventBus.$emit(GlobalEvents.pendingApprovalsChanged.externalCreatives, result.totalCount);
      }
    } finally {
      isLoading.value = false;
    }
  }

  async function validateExternalCreatives() {
    let isValid = true;
    const selectedCreatives = selectedCreativeFiles.value.map((creativeFile) =>
      creatives.value.find((creative) => creative.id === creativeFile.creativeID),
    );
    const $validator = new Validator(getAppInstance());
    try {
      for (const creative of selectedCreatives) {
        const extras = {} as Record<string, { message: string; preEvaluated: boolean }>;
        $validator.init(creative);

        if (!creative.languageIDs || creative.languageIDs.length === 0) {
          extras.languages = {
            message: i18n.global.t('messages.creativeLanguagesForItemIsEmpty', [creative.name]),
            preEvaluated: true,
          };
        }
        await $validator.validateAll(creative, extras);
      }
    } catch {
      isValid = false;
      const message = `${i18n.global.t('messages.failedToApproveCreativeFiles')}<br/>${$validator.messageBag.htmlErrorList}`;
      UserNotifications.error({
        title: i18n.global.t('messages.missingRequiredValue'),
        message,
      });
    }
    return isValid;
  }

  async function approveSelectedCreativeFiles() {
    const validity = await validateExternalCreatives();
    if (!validity) {
      return;
    }

    const legacyCreativeFiles = selectedCreativeFiles.value.filter((creativeFile) =>
      creatives.value.find(
        (creative) => creative.id === creativeFile.creativeID && creative.mediaOwnerApprovals.length === 0,
      ),
    );
    const creativeFilesReadyForComplexApproval = selectedCreativeFiles.value.filter((creativeFile) =>
      creatives.value.find(
        (creative) => creative.id === creativeFile.creativeID && creative.mediaOwnerApprovals.length > 0,
      ),
    );
    if (legacyCreativeFiles.length > 0) {
      await approveLegacyExternalCreativesByCreativeFiles(legacyCreativeFiles);
      UserNotifications.success({
        title: capitalize(i18n.global.t('names.success')),
        message: i18n.global.t('messages.creativeApprovedSuccessfully', {
          n: legacyCreativeFiles.length,
        }),
      });
    }
    if (creativeFilesReadyForComplexApproval.length > 0) {
      const creativeMediaApprovals = CreativeMediaOwnerApprovalService.getCreativeFilesMediaOwnerApprovals(
        creativeFilesReadyForComplexApproval,
        creatives.value,
      );
      const creativeMediaApprovalIDs = creativeMediaApprovals.map((x) => x.id);
      await CreativeMediaOwnerApprovalService.approveCreativeMediaApprovals(creativeMediaApprovalIDs);
    }

    onApprovalChanged();
  }

  /**
   * In the case of legacy external creatives, we need to approve every selected creative files. This is the old way of approving external creatives.
   */
  async function approveLegacyExternalCreativesByCreativeFiles(creativeFiles: CreativeFile[]) {
    const creativeFileIDs = creativeFiles.map((x) => x.id);
    await updateCreativeFilePrefetchApprovalState({
      creativeFileIDs,
      newState: CreativeFileApprovalStatus.APPROVED,
    });
    UserNotifications.success({
      title: capitalize(i18n.global.t('names.success')),
      message: i18n.global.t('messages.creativeApprovedSuccessfully', {
        n: creativeFiles.length,
      }),
    });
  }

  async function rejectSelectedCreativeFiles() {
    const creativeFileIDs = selectedCreativeFiles.value.map((x) => x.id);
    await updateCreativeFilePrefetchApprovalState({
      creativeFileIDs,
      newState: CreativeFileApprovalStatus.REJECTED,
    });
    UserNotifications.success({
      title: capitalize(i18n.global.t('names.success')),
      message: i18n.global.t('messages.creativeRejectedSuccessfully', {
        n: creativeFileIDs.length,
      }),
    });
    onApprovalChanged();
  }

  async function approveCreativeFilesForSpecificInventory(creativeFiles: CreativeFile[] = selectedCreativeFiles.value) {
    const creativeMediaApprovals = CreativeMediaOwnerApprovalService.getCreativeFilesMediaOwnerApprovals(
      creativeFiles,
      creatives.value,
    );
    const creativeMediaApprovalIDs = creativeMediaApprovals.map((x) => x.id);
    const approved = await inventoryTargetingModal.value.approveForSpecificInventory(creativeMediaApprovalIDs);
    if (approved) {
      onApprovalChanged();
    }
  }

  function onSelectedCreativeFilesChanged(creative: Creative, selectedCreativeFiles: CreativeFile[]) {
    selectedCreativeFilesByCreative.value[creative.id] = selectedCreativeFiles;
  }

  function onApprovalChanged() {
    emits('approval-changed');
    refresh();
  }

  function refresh() {
    creativesApprovalCore.value?.refresh();
  }

  defineExpose({
    refresh,
  });
</script>
<style lang="scss" rel="stylesheet/scss" scoped>
  @use '@/styles/variables' as *;

  .item-name-cell {
    display: flex;
    align-items: center;
    .selected-items-count-badge {
      display: flex;
      align-items: center;
      justify-content: center;
      background-color: red;
      padding: 0.5rem;
      border-radius: 0.8rem;
      height: 1.8rem;
      width: 1.8rem;
      color: #fff;
      margin-right: 1rem;
      font-weight: 700;
      font-size: 1.1rem;
    }
  }
</style>
